import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { buildDataTableLabels } from "@/components/table";
import { Link, useSearchParams } from "react-router";
import {
  ArrowUpRight,
  CircleDot,
  CreditCard,
  Lock,
  PencilLine,
  Plug,
  Plus,
  RefreshCw,
  Settings2,
  Trash2,
  Unlink,
  Unplug,
  Wifi,
  WifiOff,
} from "lucide-react";
import { toast } from "react-hot-toast";
import {
  Badge,
  Button,
  ConfirmModal,
  HoverPopover,
  Spinner,
  Switch,
  Tabs,
  type BadgeVariant,
} from "@/components/ui";
import {
  DataTable,
  DataTablePrimaryText,
  createActionsColumn,
  type DataTableColumn,
} from "@/components/table";
import { PageFrame, ListToolbar, buildListResetToken } from "@/components/common";
import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import { AdminRegionFilter } from "@/components/admin/AdminRegionFilter";
import { RegionCodeBadge } from "@/components/admin/RegionCodeBadge";
import { getApiErrorMessage, toastApiError } from "@/lib/errorMessages";
import { getBatteryIcon } from "@/lib/deviceStatus";
import {
  formatReaderSerialNumber,
  readerSerialMatchesQuery,
} from "@/lib/readerSerialNumber";
import { matchesTextQuery } from "@/lib/textSearch";
import { deviceKeys, deviceService } from "@/services/deviceService";
import { readerKeys, readerService } from "@/services/readerService";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import { badgeVariantFromConfig } from "@/lib/statusBadgeFromConfig";
import type { DeviceStatusSnapshot, ReaderInventory } from "@/types";
import { Permission, ReaderDeviceState, ReaderStatus } from "@/types";
import { useAuthStore } from "@/store/authStore";
import { ChangeReaderRegionModal } from "./components/ChangeReaderRegionModal";
import { CreateReaderModal } from "./components/CreateReaderModal";
import { PairReaderModal } from "./components/PairReaderModal";
import { RenameReaderModal } from "./components/RenameReaderModal";
import ReaderPresetsPage from "./presets";
import { cn } from "@/lib/utils";

const iconButtonClass =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50";
/** Inline edit pencil: quiet until its cell is hovered or focused. */
const inlineEditButtonClass = `${iconButtonClass} h-7 w-7 text-surface-400 opacity-0 transition-opacity group-hover/cell:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100`;

type PairingFilter = "all" | "not_paired" | typeof ReaderStatus.PAIRED;
type DeviceStatusBucket = "online" | "unavailable" | "offline" | "unknown";
type DeviceStatusFilter = "all" | DeviceStatusBucket;

type SortColumn = "brand" | "serial" | "region" | "status" | "device";

const PAIRING_FILTER_OPTIONS: PairingFilter[] = [
  "all",
  "not_paired",
  ReaderStatus.PAIRED,
];

const PAIRING_FILTER_LABEL_KEYS: Record<PairingFilter, string> = {
  all: "page.filterStatusAll",
  not_paired: "page.statusNotPaired",
  [ReaderStatus.PAIRED]: "page.statusPaired",
};

const PAIRING_FILTER_DOT_CLASS: Record<Exclude<PairingFilter, "all">, string> = {
  not_paired: "bg-surface-400",
  [ReaderStatus.PAIRED]: "bg-success-500",
};

const DEVICE_STATUS_FILTER_OPTIONS: DeviceStatusFilter[] = [
  "all",
  "online",
  "unavailable",
  "offline",
  "unknown",
];

const DEVICE_STATUS_FILTER_LABEL_KEYS: Record<DeviceStatusFilter, string> = {
  all: "page.filterStatusAll",
  online: "page.filterDeviceOnline",
  unavailable: "page.filterDeviceUnavailable",
  offline: "page.deviceOffline",
  unknown: "page.filterDeviceUnknown",
};

const DEVICE_STATUS_FILTER_DOT_CLASS: Record<
  Exclude<DeviceStatusFilter, "all">,
  string
> = {
  online: "bg-success-500",
  unavailable: "bg-warning-400",
  offline: "bg-error-500",
  unknown: "bg-surface-400",
};

const READER_STATUS_BADGE = {
  [ReaderStatus.CREATED]: { badgeVariant: "default" },
  [ReaderStatus.NOT_PAIRED]: { badgeVariant: "default" },
  [ReaderStatus.PAIRED]: { badgeVariant: "success" },
} as const satisfies Record<ReaderStatus, { badgeVariant: BadgeVariant }>;

const READER_DEVICE_STATES = Object.values(ReaderDeviceState);

/** Online+available (green). UPDATING_FIRMWARE → unavailable (amber). */
const AVAILABLE_DEVICE_STATES = new Set<string>(
  READER_DEVICE_STATES.filter(
    (state) => state !== ReaderDeviceState.UPDATING_FIRMWARE,
  ),
);
const KNOWN_DEVICE_STATE_KEYS = new Set<string>(READER_DEVICE_STATES);

function classifyPairedSumupDeviceStatus(
  snap: DeviceStatusSnapshot | null,
): { bucket: DeviceStatusBucket; badgeVariant: BadgeVariant } | null {
  if (!snap?.status) return null;
  if (snap.status !== "ONLINE") {
    return { bucket: "offline", badgeVariant: "error" };
  }
  if (!snap.state || AVAILABLE_DEVICE_STATES.has(snap.state)) {
    return { bucket: "online", badgeVariant: "success" };
  }
  return { bucket: "unavailable", badgeVariant: "warning" };
}

/** Null when the reader is not eligible for device-status filtering (not SumUp+PAIRED). */
function readerDeviceStatusClassification(
  reader: ReaderInventory,
): { bucket: DeviceStatusBucket; badgeVariant: BadgeVariant } | null {
  if (
    reader.provider.toUpperCase() !== "SUMUP" ||
    reader.status !== ReaderStatus.PAIRED
  ) {
    return null;
  }
  return (
    classifyPairedSumupDeviceStatus(reader.device_status_snapshot) ?? {
      bucket: "unknown",
      badgeVariant: "default",
    }
  );
}

function getReaderBatteryClass(level: number): string {
  if (level < 10) return "text-error-600";
  if (level < 25) return "text-warning-600";
  if (level > 70) return "text-success-600";
  return "text-surface-500";
}

function matchesPairingFilter(
  reader: ReaderInventory,
  pairingFilter: PairingFilter,
): boolean {
  if (pairingFilter === "all") return true;
  if (pairingFilter === "not_paired") {
    return (
      reader.status === ReaderStatus.CREATED ||
      reader.status === ReaderStatus.NOT_PAIRED
    );
  }
  return reader.status === pairingFilter;
}

function canPair(reader: ReaderInventory) {
  return (
    reader.provider.toUpperCase() === "SUMUP" &&
    reader.status !== ReaderStatus.PAIRED &&
    !reader.assigned_device
  );
}

function canUnpair(reader: ReaderInventory) {
  return (
    reader.provider.toUpperCase() === "SUMUP" &&
    reader.status === ReaderStatus.PAIRED &&
    !reader.assigned_device
  );
}

function canChangeRegion(reader: ReaderInventory) {
  return !reader.assigned_device && !reader.region_locked_at;
}

function canDelete(reader: ReaderInventory) {
  return !reader.assigned_device;
}

function canUnlinkFromDevice(reader: ReaderInventory) {
  return Boolean(reader.assigned_device);
}

export default function ReadersPage() {
  const { t } = useTranslation("readers");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const focusReaderId = searchParams.get("readerId");
  const didApplyReaderFocusRef = useRef(false);
  const canHardDelete = useAuthStore(
    (s) =>
      s.hasPermission(Permission.PLATFORM_HARD_DELETE) &&
      s.hasPermission(Permission.READERS_UPDATE),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [pairingFilter, setPairingFilter] = useState<PairingFilter>("all");
  const [deviceStatusFilter, setDeviceStatusFilter] =
    useState<DeviceStatusFilter>("all");
  const [regionFilter, setRegionFilter] = useState<string | null>(null);
  const [showMockReaders, setShowMockReaders] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    kind: "pair" | "region" | "rename" | "delete" | "unpair" | "unlink";
    reader: ReaderInventory;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [activeTab, setActiveTab] = useState<"readers" | "presets">("readers");
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);

  const busyReaderId =
    actionBusy && pendingAction ? pendingAction.reader.id : null;

  const readerListParams = { region_id: regionFilter ?? undefined };
  const {
    data: readers = [],
    isLoading,
    error: readersQueryError,
    refetch: refetchReaders,
  } = useQuery({
    queryKey: readerKeys.list(readerListParams),
    queryFn: () => readerService.getAll(readerListParams),
    enabled: activeTab === "readers",
  });
  const loadError = readersQueryError
    ? getApiErrorMessage(readersQueryError, "loadReaders")
    : null;

  useEffect(() => {
    didApplyReaderFocusRef.current = false;
  }, [focusReaderId]);

  useEffect(() => {
    if (!focusReaderId || didApplyReaderFocusRef.current || isLoading) {
      return;
    }
    const reader = readers.find((item) => item.id === focusReaderId);
    if (!reader) {
      if (regionFilter !== null) {
        setRegionFilter(null);
      }
      return;
    }
    didApplyReaderFocusRef.current = true;
    setActiveTab("readers");
    setPairingFilter("all");
    setDeviceStatusFilter("all");
    setShowMockReaders(true);
    setSearchQuery(reader.serial_number);
  }, [focusReaderId, isLoading, readers, regionFilter]);

  const invalidateReaderLists = async () => {
    await queryClient.invalidateQueries({ queryKey: readerKeys.lists() });
  };

  const searchMatched = useMemo(() => {
    const query = searchQuery.trim();
    return readers.filter((reader) => {
      if (!showMockReaders && reader.provider.toUpperCase() === "MOCK") {
        return false;
      }
      if (!query) return true;
      return (
        readerSerialMatchesQuery(reader.serial_number, query) ||
        matchesTextQuery(query, [
          reader.brand,
          reader.model,
          reader.provider,
          reader.assigned_device?.display_name,
        ], { trim: false })
      );
    });
  }, [readers, searchQuery, showMockReaders]);

  const filteredReaders = useMemo(() => {
    return searchMatched.filter((reader) => {
      if (!matchesPairingFilter(reader, pairingFilter)) return false;
      if (deviceStatusFilter === "all") return true;
      const classified = readerDeviceStatusClassification(reader);
      return classified?.bucket === deviceStatusFilter;
    });
  }, [searchMatched, pairingFilter, deviceStatusFilter]);

  const pairingFilterCounts = useMemo(() => {
    const counts: Record<PairingFilter, number> = {
      all: 0,
      not_paired: 0,
      [ReaderStatus.PAIRED]: 0,
    };
    for (const reader of searchMatched) {
      if (deviceStatusFilter !== "all") {
        const classified = readerDeviceStatusClassification(reader);
        if (classified?.bucket !== deviceStatusFilter) continue;
      }
      counts.all += 1;
      if (reader.status === ReaderStatus.PAIRED) {
        counts[ReaderStatus.PAIRED] += 1;
      } else if (
        reader.status === ReaderStatus.CREATED ||
        reader.status === ReaderStatus.NOT_PAIRED
      ) {
        counts.not_paired += 1;
      }
    }
    return counts;
  }, [searchMatched, deviceStatusFilter]);

  const deviceStatusFilterCounts = useMemo(() => {
    const counts: Record<DeviceStatusFilter, number> = {
      all: 0,
      online: 0,
      unavailable: 0,
      offline: 0,
      unknown: 0,
    };
    for (const reader of searchMatched) {
      if (!matchesPairingFilter(reader, pairingFilter)) continue;
      counts.all += 1;
      const classified = readerDeviceStatusClassification(reader);
      if (classified) {
        counts[classified.bucket] += 1;
      }
    }
    return counts;
  }, [searchMatched, pairingFilter]);

  const handleConfirmPendingAction = async () => {
    if (!pendingAction) return;
    const { kind, reader } = pendingAction;
    if (kind !== "delete" && kind !== "unpair" && kind !== "unlink") return;
    const assignedDevice = reader.assigned_device;
    if (kind === "unlink" && assignedDevice == null) return;
    setActionBusy(true);
    try {
      if (kind === "unpair") {
        await readerService.unpairSumup(reader.id, true);
        toast.success(t("page.readerUnpaired"));
      } else if (kind === "unlink") {
        if (assignedDevice == null) return;
        await deviceService.unlinkReader(assignedDevice.id);
        toast.success(t("page.readerUnlinkedFromDevice"));
      } else {
        await readerService.delete(reader.id);
        toast.success(t("page.readerDeleted"));
      }
      setPendingAction(null);
      await invalidateReaderLists();
      if (kind === "unlink") {
        await queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      }
    } catch (err) {
      toastApiError(err, "readerOperation");
    } finally {
      setActionBusy(false);
    }
  };

  const handleRefreshAllDeviceStatuses = async () => {
    setIsRefreshingAll(true);
    try {
      const { updated, failed } = await readerService.refreshAllDeviceStatuses();
      if (updated > 0 && failed === 0) {
        toast.success(t("page.deviceStatusesRefreshed"));
      } else if (updated > 0 && failed > 0) {
        toast(t("page.deviceStatusesRefreshedPartial", { updated, failed }));
      } else if (updated === 0 && failed > 0) {
        toast.error(t("page.deviceStatusesRefreshFailed", { failed }));
      } else {
        toast.success(t("page.deviceStatusesNothingToRefresh"));
      }
      await invalidateReaderLists();
    } catch (err) {
      toastApiError(err, "readerOperation");
    } finally {
      setIsRefreshingAll(false);
    }
  };

  const deleteSerialLabel =
    pendingAction?.kind === "delete"
      ? formatReaderSerialNumber(
          pendingAction.reader.serial_number,
          pendingAction.reader.provider,
        )
      : "";

  const deleteConfirmMessage =
    pendingAction?.kind === "delete" &&
    pendingAction.reader.provider.toUpperCase() === "SUMUP" &&
    pendingAction.reader.status === ReaderStatus.PAIRED
      ? t("page.deleteConfirmPaired", { serial: deleteSerialLabel })
      : t("page.deleteConfirm", { serial: deleteSerialLabel });

  const columns = useMemo<DataTableColumn<ReaderInventory, SortColumn>[]>(
    () => [
      {
        id: "brand",
        header: t("page.colBrand"),
        sortKey: "brand",
        sortKind: "text",
        getSortValue: (row) => row.brand,
        defaultWidth: 200,
        cell: (reader) => (
          <span className="flex min-w-0 items-center gap-2.5">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-600/15"
              aria-hidden
            >
              <CreditCard className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <DataTablePrimaryText className="block truncate">
                {reader.brand}
              </DataTablePrimaryText>
              <span className="mt-0.5 block truncate text-xs text-surface-500">
                {[reader.model, reader.provider].filter(Boolean).join(" · ")}
              </span>
            </span>
          </span>
        ),
      },
      {
        id: "serial",
        header: t("page.colSerial"),
        sortKey: "serial",
        sortKind: "text",
        getSortValue: (row) => row.serial_number,
        defaultWidth: 180,
        cell: (reader) => {
          const isBusy = busyReaderId === reader.id;
          return (
            <div className="group/cell flex min-w-0 items-center gap-1">
              <ClickToCopyId
                className="text-[13px]"
                value={reader.serial_number}
                displayValue={formatReaderSerialNumber(
                  reader.serial_number,
                  reader.provider,
                )}
                ellipsis="start"
              />
              <HoverPopover content={t("renameModal.title")}>
                <button
                  type="button"
                  className={cn(inlineEditButtonClass, isBusy && "opacity-100")}
                  disabled={isBusy}
                  onClick={() => setPendingAction({ kind: "rename", reader })}
                  aria-label={t("renameModal.title")}
                >
                  {isBusy ? (
                    <Spinner size="sm" variant="muted"  loadingLabel={tCommon("loading")} />
                  ) : (
                    <PencilLine className="h-4 w-4" />
                  )}
                </button>
              </HoverPopover>
            </div>
          );
        },
      },
      {
        id: "region",
        header: t("page.colRegion"),
        sortKey: "region",
        sortKind: "text",
        getSortValue: (row) => row.region_code,
        defaultWidth: 120,
        cell: (reader) => {
          const isBusy = busyReaderId === reader.id;
          return (
            <div className="group/cell flex items-center gap-1">
              {reader.region_locked_at ? (
                <HoverPopover content={t("page.regionLockedHint")}>
                  <Badge
                    variant="warning"
                    size="sm"
                    aria-label={`${reader.region_code ?? t("page.regionLocked")} — ${t("page.regionLocked")}`}
                  >
                    {reader.region_code}
                    <Lock aria-hidden />
                  </Badge>
                </HoverPopover>
              ) : (
                <RegionCodeBadge code={reader.region_code} />
              )}
              {canChangeRegion(reader) ? (
                <HoverPopover content={t("page.changeRegionAction")}>
                  <button
                    type="button"
                    className={cn(inlineEditButtonClass, isBusy && "opacity-100")}
                    disabled={isBusy}
                    onClick={() => setPendingAction({ kind: "region", reader })}
                    aria-label={t("page.changeRegionAction")}
                  >
                    {isBusy ? (
                      <Spinner size="sm" variant="muted"  loadingLabel={tCommon("loading")} />
                    ) : (
                      <PencilLine className="h-4 w-4" />
                    )}
                  </button>
                </HoverPopover>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "status",
        header: t("page.colStatus"),
        sortKey: "status",
        sortKind: "text",
        getSortValue: (row) =>
          `${row.status} ${row.device_status_snapshot?.status ?? ""} ${row.device_status_snapshot?.state ?? ""}`,
        defaultWidth: 220,
        cell: (reader) => {
          const pairingLabel =
            reader.status === ReaderStatus.CREATED
              ? t("page.statusCreated")
              : reader.status === ReaderStatus.NOT_PAIRED
                ? t("page.statusNotPaired")
                : t("page.statusPaired");
          const snap =
            reader.provider.toUpperCase() === "SUMUP" &&
            reader.status === ReaderStatus.PAIRED
              ? reader.device_status_snapshot
              : null;
          const classified = classifyPairedSumupDeviceStatus(snap);
          const liveLabel = !snap
            ? null
            : snap.state
              ? KNOWN_DEVICE_STATE_KEYS.has(snap.state)
                ? t(`page.deviceState.${snap.state}`)
                : t("page.deviceState.UNKNOWN")
              : t(
                  classified?.bucket === "offline"
                    ? "page.deviceOffline"
                    : "page.deviceOnline",
                );
          const liveVariant = classified?.badgeVariant ?? null;
          const batteryLevel = snap?.battery_level;
          const batteryRounded =
            batteryLevel != null ? Math.round(batteryLevel) : null;
          const BatteryIcon =
            batteryRounded != null
              ? getBatteryIcon(batteryRounded)
              : null;
          const batteryClass =
            batteryRounded != null
              ? getReaderBatteryClass(batteryRounded)
              : undefined;
          const checkedAt = snap?.checked_at
            ? new Date(snap.checked_at).toLocaleString()
            : undefined;
          const hoverContent =
            snap &&
            (checkedAt ||
              snap.last_activity ||
              snap.connection_type ||
              batteryRounded != null) ? (
              <div className="space-y-0.5 text-xs">
                {checkedAt && (
                  <div>{t("page.deviceLastChecked", { time: checkedAt })}</div>
                )}
                {snap.last_activity && (
                  <div>
                    {t("page.deviceLastActivity", {
                      time: new Date(snap.last_activity).toLocaleString(),
                    })}
                  </div>
                )}
                {snap.connection_type && <div>{snap.connection_type}</div>}
                {batteryRounded != null && (
                  <div>{t("page.deviceBattery", { level: batteryRounded })}</div>
                )}
              </div>
            ) : null;

          const content = (
            <div className="inline-flex min-w-0 items-center gap-1.5">
              {liveLabel && liveVariant ? (
                <Badge variant={liveVariant} size="sm">
                  {liveVariant === "error" ? (
                    <WifiOff aria-hidden />
                  ) : (
                    <Wifi aria-hidden />
                  )}
                  {liveLabel}
                </Badge>
              ) : (
                <Badge
                  variant={badgeVariantFromConfig(
                    reader.status,
                    READER_STATUS_BADGE,
                  )}
                  size="sm"
                >
                  {pairingLabel}
                </Badge>
              )}
              {BatteryIcon && batteryRounded != null && batteryClass && (
                <span
                  className={`inline-flex h-5 items-center gap-1 text-xs font-medium leading-none tabular-nums ${batteryClass}`}
                  aria-label={t("page.deviceBattery", { level: batteryRounded })}
                >
                  <BatteryIcon className="block size-[1em] shrink-0" aria-hidden />
                  <span>{batteryRounded}%</span>
                </span>
              )}
            </div>
          );

          if (!hoverContent) return content;
          return <HoverPopover content={hoverContent}>{content}</HoverPopover>;
        },
      },
      {
        id: "device",
        header: t("page.colDevice"),
        sortKey: "device",
        sortKind: "text",
        getSortValue: (row) => row.assigned_device?.display_name,
        defaultWidth: 160,
        cell: (reader) =>
          reader.assigned_device ? (
            <Link
              to={`/devices?deviceId=${reader.assigned_device.id}`}
              className="inline-flex min-w-0 max-w-full items-center gap-1 text-primary-700 hover:text-primary-900 hover:underline"
              aria-label={t("page.openAssignedDevice", {
                name: reader.assigned_device.display_name,
              })}
            >
              <span
                className="min-w-0 truncate"
                title={reader.assigned_device.display_name}
              >
                {reader.assigned_device.display_name}
              </span>
              <ArrowUpRight
                className="h-3.5 w-3.5 shrink-0"
                aria-hidden
              />
            </Link>
          ) : (
            <span className="text-surface-300" aria-hidden>
              —
            </span>
          ),
      },
      createActionsColumn(t("page.colActions"), {
        align: "left",
        defaultWidth: 140,
        cell: (reader) => {
          const isBusy = busyReaderId === reader.id;
          return (
            <div className="flex items-center justify-start gap-0.5">
              {canPair(reader) ? (
                <HoverPopover content={t("page.pairAction")}>
                  <button
                    type="button"
                    className={iconButtonClass}
                    disabled={isBusy}
                    onClick={() => setPendingAction({ kind: "pair", reader })}
                    aria-label={t("page.pairAction")}
                  >
                    {isBusy ? (
                      <Spinner size="sm" variant="muted" loadingLabel={tCommon("loading")} />
                    ) : (
                      <Plug className="h-4 w-4" />
                    )}
                  </button>
                </HoverPopover>
              ) : null}
              {canUnpair(reader) ? (
                <HoverPopover content={t("page.unpairAction")}>
                  <button
                    type="button"
                    className={`${iconButtonClass} text-error-600 hover:bg-error-50 hover:text-error-700 dark:hover:text-error-800`}
                    disabled={isBusy}
                    onClick={() =>
                      setPendingAction({ kind: "unpair", reader })
                    }
                    aria-label={t("page.unpairAction")}
                  >
                    {isBusy ? (
                      <Spinner size="sm" variant="muted" loadingLabel={tCommon("loading")} />
                    ) : (
                      <Unplug className="h-4 w-4" />
                    )}
                  </button>
                </HoverPopover>
              ) : null}
              {canUnlinkFromDevice(reader) ? (
                <HoverPopover content={t("page.unlinkFromDeviceAction")}>
                  <button
                    type="button"
                    className={`${iconButtonClass} text-error-600 hover:bg-error-50 hover:text-error-700 dark:hover:text-error-800`}
                    disabled={isBusy}
                    onClick={() =>
                      setPendingAction({ kind: "unlink", reader })
                    }
                    aria-label={t("page.unlinkFromDeviceAction")}
                  >
                    {isBusy ? (
                      <Spinner size="sm" variant="muted" loadingLabel={tCommon("loading")} />
                    ) : (
                      <Unlink className="h-4 w-4" />
                    )}
                  </button>
                </HoverPopover>
              ) : null}
              {canHardDelete && canDelete(reader) ? (
                <HoverPopover content={t("page.deleteTitle")}>
                  <button
                    type="button"
                    className={`${iconButtonClass} text-error-600 hover:bg-error-50 hover:text-error-700 dark:hover:text-error-800`}
                    disabled={isBusy}
                    onClick={() =>
                      setPendingAction({ kind: "delete", reader })
                    }
                    aria-label={t("page.deleteTitle")}
                  >
                    {isBusy ? (
                      <Spinner size="sm" variant="muted" loadingLabel={tCommon("loading")} />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </HoverPopover>
              ) : null}
            </div>
          );
        },
      }),
    ],
    [busyReaderId, canHardDelete, t, tCommon],
  );

  return (
    <PageFrame
      title={t("page.title")}
      description={t("page.subtitle")}
      icon={activeTab === "presets" ? Settings2 : CreditCard}
      actions={
        activeTab === "readers" ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => void handleRefreshAllDeviceStatuses()}
              disabled={isRefreshingAll}
              isLoading={isRefreshingAll}
              leftIcon={<RefreshCw className="h-4 w-4" />}
            >
              {t("page.refreshAllDeviceStatuses")}
            </Button>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              {t("page.newReader")}
            </Button>
          </div>
        ) : undefined
      }
    >

      <Tabs
        variant="segmented"
        ariaLabel={t("page.title")}
        idPrefix="readers"
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={[
          {
            id: "readers",
            label: t("page.tabReaders"),
            icon: <CreditCard className="h-4 w-4" />,
          },
          {
            id: "presets",
            label: t("page.tabPresets"),
            icon: <Settings2 className="h-4 w-4" />,
          },
        ]}
      />

      {activeTab === "presets" ? (
        <ReaderPresetsPage />
      ) : (
        <>
          <ListToolbar
            search={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={t("page.searchPlaceholder")}
            searchAccessory={
              <Switch
                checked={showMockReaders}
                onChange={(e) => setShowMockReaders(e.target.checked)}
                label={t("page.showMockReaders")}
              />
            }
            filterChildren={
              <>
                <AdminRegionFilter
                  value={regionFilter}
                  onChange={setRegionFilter}
                  valueKey="id"
                />
                <AdminFilterMenu
                  icon={CircleDot}
                  name={t("page.filterPairing")}
                  value={pairingFilter}
                  defaultValue="all"
                  onChange={setPairingFilter}
                  options={PAIRING_FILTER_OPTIONS.map((option) => ({
                    value: option,
                    label: t(PAIRING_FILTER_LABEL_KEYS[option]),
                    count: pairingFilterCounts[option],
                    dotClassName:
                      option === "all"
                        ? undefined
                        : PAIRING_FILTER_DOT_CLASS[option],
                  }))}
                />
                <AdminFilterMenu
                  icon={Wifi}
                  name={t("page.filterDeviceStatus")}
                  value={deviceStatusFilter}
                  defaultValue="all"
                  onChange={setDeviceStatusFilter}
                  options={DEVICE_STATUS_FILTER_OPTIONS.map((option) => ({
                    value: option,
                    label: t(DEVICE_STATUS_FILTER_LABEL_KEYS[option]),
                    count: deviceStatusFilterCounts[option],
                    dotClassName:
                      option === "all"
                        ? undefined
                        : DEVICE_STATUS_FILTER_DOT_CLASS[option],
                  }))}
                />
              </>
            }
          />

          <DataTable
            labels={tableLabels}
            closeLabel={tCommon("close")}
            loadingLabel={tCommon("loading")}
            rows={filteredReaders}
            columns={columns}
            getRowKey={(row) => row.id}
            defaultSortColumn="brand"
            preferencesKey="readers"
            resetToken={buildListResetToken([
              searchQuery,
              pairingFilter,
              deviceStatusFilter,
              regionFilter ?? "",
              showMockReaders,
            ])}
            loadState={
              isLoading
                ? { status: "loading" }
                : loadError != null
                  ? {
                      status: "error",
                      message: loadError,
                      retryLabel: tCommon("tryAgain"),
                      onRetry: () => void refetchReaders(),
                    }
                  : undefined
            }
            emptyMessage={
              searchQuery.trim() ||
              pairingFilter !== "all" ||
              deviceStatusFilter !== "all"
                ? t("page.emptySearch")
                : t("page.emptyDefault")
            }
          />
        </>
      )}

      <CreateReaderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={() => invalidateReaderLists()}
        preferredRegionId={regionFilter}
      />
      <RenameReaderModal
        isOpen={pendingAction?.kind === "rename"}
        reader={pendingAction?.kind === "rename" ? pendingAction.reader : null}
        onClose={() => setPendingAction(null)}
        onRenamed={() => invalidateReaderLists()}
      />
      <PairReaderModal
        isOpen={pendingAction?.kind === "pair"}
        reader={pendingAction?.kind === "pair" ? pendingAction.reader : null}
        onClose={() => setPendingAction(null)}
        onPaired={() => invalidateReaderLists()}
      />
      <ChangeReaderRegionModal
        isOpen={pendingAction?.kind === "region"}
        reader={pendingAction?.kind === "region" ? pendingAction.reader : null}
        onClose={() => setPendingAction(null)}
        onChanged={() => invalidateReaderLists()}
      />
      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        isOpen={
          pendingAction?.kind === "delete" ||
          pendingAction?.kind === "unpair" ||
          pendingAction?.kind === "unlink"
        }
        onClose={() => {
          if (!actionBusy) setPendingAction(null);
        }}
        onConfirm={() => void handleConfirmPendingAction()}
        title={
          pendingAction?.kind === "delete"
            ? t("page.deleteTitle")
            : pendingAction?.kind === "unpair"
              ? t("page.unpairAction")
              : t("page.unlinkFromDeviceAction")
        }
        message={
          pendingAction?.kind === "delete"
            ? deleteConfirmMessage
            : pendingAction?.kind === "unpair"
              ? t("page.unpairConfirm", {
                  serial: pendingAction.reader.serial_number,
                })
              : t("page.unlinkFromDeviceConfirm", {
                  serial: pendingAction
                    ? formatReaderSerialNumber(
                        pendingAction.reader.serial_number,
                        pendingAction.reader.provider,
                      )
                    : "",
                  device: pendingAction?.reader.assigned_device?.display_name ?? "",
                })
        }
        confirmText={
          pendingAction?.kind === "delete"
            ? tCommon("delete")
            : pendingAction?.kind === "unpair"
              ? t("page.unpairAction")
              : t("page.unlinkFromDeviceAction")
        }
        isDestructive={pendingAction?.kind !== "unpair"}
        isLoading={actionBusy}
      />
    </PageFrame>
  );
}
